import {
    Autocomplete,
    Box,
    TextField,
    createFilterOptions,
    FilterOptionsState,
    paletteColors,
    Typography,
    Stack,
} from '@evaneos/b2b-ui';
import React, { useState, FormEvent } from 'react';
import { useIntl } from 'react-intl';

import { DossierId, DossierTag } from '../../../../resources/dossier/types';
import {
    IncludedTag,
    useAssignTagToDossier,
    useCreateTag,
    useUnassignTagToDossier,
    useGetAllTags,
} from '../../../../resources/tags/api/api';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

import { TAG_CHIP_COLOR, TagChip } from './TagChip';

const filter = createFilterOptions<Option>();

type Option = {
    value: string;
    label: string;
    isPlaceholder?: boolean;
    isNew?: boolean;
};

const TAG_LIMIT = 3;

type TagsEditableItemProps = {
    dossierId: DossierId;
    labelId: string;
    tags: DossierTag[];
};

const tagToOption = (tag: DossierTag | IncludedTag): Option => ({
    label: 'attributes' in tag ? tag.attributes.name : tag.name,
    value: tag.id,
});

const isExistingLabel = (options: Option[], newOptionLabel: string): boolean =>
    options.some((option) => option.label === newOptionLabel.trim());

/* eslint-disable max-lines-per-function -- tag editor with create/assign/unassign mutations and inline creation */
export function TagsEditableItem({ dossierId, labelId, tags }: Readonly<TagsEditableItemProps>) {
    const { formatMessage } = useIntl();
    const allTags = useGetAllTags();
    const createTagMutation = useCreateTag();
    const assignTagMutation = useAssignTagToDossier();
    const unassignTagMutation = useUnassignTagToDossier();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const allTagOptions = allTags.data?.data?.map(tagToOption) || [];

    const [selectedOptions, setSelectedOptions] = useState(tags.map(tagToOption));
    const [inputValue, setInputValue] = useState('');

    const handleAssignTag = (newOptions: Option[]) => {
        const newTag = newOptions.at(-1);

        if (!newTag) {
            return;
        }

        if (newTag.isNew) {
            createTagMutation.mutate(
                {
                    name: newTag.label,
                    color: TAG_CHIP_COLOR,
                    dossierId: parseInt(dossierId),
                },
                {
                    onSuccess: (response, request) => {
                        const newSelectedOptions = newOptions.map((option) => {
                            if (option.label !== request.name) {
                                return option;
                            }
                            return { ...option, value: response.data.id };
                        });
                        setSelectedOptions(newSelectedOptions);
                    },
                    onError: (_response, request) => {
                        const newSelectedOptions = newOptions.filter(
                            (option) => option.label !== request.name,
                        );
                        setSelectedOptions(newSelectedOptions);
                    },
                },
            );
            trackEvent((ampli, defaultProperties) => {
                ampli.newTagAdded({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    tag_file_content: newTag.label,
                });
            });
            return;
        }

        assignTagMutation.mutate({
            tagId: newTag.value,
            dossierId: parseInt(dossierId),
        });
        trackEvent((ampli, defaultProperties) => {
            ampli.tagSelected({
                ...defaultProperties,
                ...trackedTripProperties,
                tag_file_content: newTag.label,
            });
        });
    };

    const handleUnassignTag = (newOptions: Option[]) => {
        const findMissingOption = (list1: Option[], list2: Option[]): Option | undefined => {
            return list2.find(
                // eslint-disable-next-line sonarjs/no-nested-functions -- nested callbacks required by the Array.find predicate
                (option2) => !list1.some((option1) => option1.value === option2.value),
            );
        };

        const optionToUnassign = findMissingOption(newOptions, selectedOptions);

        if (!optionToUnassign) {
            return;
        }

        unassignTagMutation.mutate({
            tagId: optionToUnassign.value,
            dossierId: parseInt(dossierId),
        });
        trackEvent((ampli, defaultProperties) => {
            ampli.tagDeleted({
                ...defaultProperties,
                ...trackedTripProperties,
                tag_file_content: optionToUnassign.label,
            });
        });
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>, close: () => void) => {
        e.preventDefault();
        close();
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLDivElement> & {
            defaultMuiPrevented?: boolean;
        },
        close: () => void,
    ) => {
        const sanitizedInput = inputValue.trim();

        if (event.key === 'Escape') {
            close();
            return;
        }

        if (event.key === 'Enter' && sanitizedInput) {
            event.preventDefault();

            if (selectedOptions.length >= TAG_LIMIT) {
                return;
            }

            if (isExistingLabel(selectedOptions, inputValue)) {
                return;
            }

            const newOption = allTagOptions.find((option) => option.label === sanitizedInput) ?? {
                label: sanitizedInput,
                value: sanitizedInput,
                isNew: true,
            };
            const newOptions = [...selectedOptions, newOption];
            setSelectedOptions(newOptions);
            handleAssignTag(newOptions);
            setInputValue('');
        }
    };

    const handleChange = (_event: React.SyntheticEvent | null, newOptions: Option[]) => {
        if (newOptions.length > selectedOptions.length && selectedOptions.length >= TAG_LIMIT) {
            return;
        }

        setSelectedOptions(newOptions);

        if (newOptions.length > selectedOptions.length) {
            handleAssignTag(newOptions);
        }

        if (newOptions.length < selectedOptions.length) {
            handleUnassignTag(newOptions);
        }
    };

    return (
        <DefinitionList.EditableValue
            labelId={labelId}
            popoverWidth="trigger"
            placement="over"
            editor={({ close }) => (
                <Box component="form" position="relative" onSubmit={(e) => handleSubmit(e, close)}>
                    <Autocomplete
                        size="small"
                        multiple
                        open
                        disableClearable
                        disablePortal
                        id="tags-standard"
                        options={allTagOptions}
                        value={selectedOptions}
                        aria-labelledby={labelId}
                        sx={{
                            '& .MuiInputBase-root': {
                                background: paletteColors.neutral[100],
                            },
                            '& .MuiInputBase-input': { fontSize: '1.4rem' },
                            '& .MuiInputBase-input::placeholder': { fontSize: '1.4rem' },
                            '& .MuiInputLabel-root': { fontSize: '1.4rem' },
                        }}
                        slotProps={{
                            listbox: { sx: { padding: 0 } },
                            paper: {
                                sx: {
                                    '& .MuiAutocomplete-option': { fontSize: '1.4rem' },
                                },
                            },
                        }}
                        getOptionDisabled={(option: Option) =>
                            !!option.isPlaceholder ||
                            selectedOptions.some(
                                (selectedOption) => selectedOption.value === option.value,
                            ) ||
                            selectedOptions.length >= TAG_LIMIT
                        }
                        onChange={handleChange}
                        inputValue={inputValue}
                        onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
                        onKeyDown={(e) => handleKeyDown(e, close)}
                        renderOption={(props, option) => {
                            if (option.isPlaceholder) {
                                return (
                                    <Box
                                        component="li"
                                        {...props}
                                        sx={{
                                            opacity: '1 !important',
                                        }}
                                        key={`${option.value}.${option.label}`}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            {option.label}
                                        </Typography>
                                    </Box>
                                );
                            }
                            if (option.isNew) {
                                if (isExistingLabel(allTagOptions, option.label)) {
                                    return;
                                }

                                return (
                                    <Box
                                        component="li"
                                        gap={1}
                                        sx={{ padding: 0.5 }}
                                        {...props}
                                        key={`${option.value}.${option.label}`}
                                    >
                                        <TagChip label={option.label} />(
                                        {formatMessage({
                                            id: 'dossier.closing_confidence.tags.new',
                                        })}
                                        )
                                    </Box>
                                );
                            }
                            return (
                                <Box
                                    component="li"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                        padding: 0.5,
                                        backgroundColor: selectedOptions.some(
                                            (selectedOption) =>
                                                selectedOption.value === option.value,
                                        )
                                            ? paletteColors.neutral[200]
                                            : 'initial',
                                    }}
                                    {...props}
                                    key={`${option.value}.${option.label}`}
                                >
                                    <TagChip label={option.label} />
                                </Box>
                            );
                        }}
                        filterOptions={(options: Option[], params: FilterOptionsState<Option>) => {
                            const filtered = filter(options, params);
                            const { inputValue } = params;

                            filtered.unshift({
                                isPlaceholder: true,
                                value: 'placeholder',
                                label: formatMessage({
                                    id: 'dossier.closing_confidence.tags.select_instruction',
                                }),
                            });
                            const sanitizedInput = inputValue.trim();

                            // Always show new tag option when typing, will be disabled if at limit
                            if (sanitizedInput) {
                                filtered.push({
                                    isNew: true,
                                    value: sanitizedInput,
                                    label: sanitizedInput,
                                });
                            }

                            return filtered;
                        }}
                        renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({
                                    index,
                                });
                                return <TagChip key={key} label={option.label} {...tagProps} />;
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                autoFocus
                                size="small"
                                variant="outlined"
                                placeholder={formatMessage({
                                    id: 'dossier.closing_confidence.tags.label',
                                })}
                            />
                        )}
                        popupIcon={null}
                    />
                </Box>
            )}
        >
            {selectedOptions.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={1} py={0.25} component="span">
                    {selectedOptions.map((option) => (
                        <TagChip key={option.value} label={option.label} />
                    ))}
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary" component="span">
                    {formatMessage({
                        id: 'dossier.closing_confidence.tags.button',
                    })}
                </Typography>
            )}
        </DefinitionList.EditableValue>
    );
}
/* eslint-enable max-lines-per-function */
